import {createContext, useContext} from 'react';
import {CLPPurchaseOption} from '../clp-purchase-context.react-component';

/**
 * The shape of the isolated selected-option context.
 * Kept separate from the main CLPPurchaseContext so that toggling the radio
 * only invalidates consumers of this context, not the entire purchase context
 * tree. This is a prerequisite for React.memo bail-outs on the expensive
 * collapsible subtrees (CollapsibleBuyBox, CollapsibleSubscriptionPurchaseContent).
 */
export interface CLPSelectedOptionContext {
    selectedPurchaseOption: CLPPurchaseOption;
    setSelectedPurchaseOption: (option: CLPPurchaseOption) => void;
}

export const ReactCLPSelectedOptionContext = createContext<CLPSelectedOptionContext | undefined>(
    undefined,
);

/**
 * Lightweight hook that subscribes only to the selected purchase option toggle state.
 * Prefer this over `useClpPurchaseContext()` in components whose only dependency on
 * the purchase context is knowing which panel is expanded — doing so prevents those
 * components from re-rendering when unrelated purchase context fields change, and
 * allows React.memo on their siblings to bail out cleanly on toggle.
 */
export function useClpSelectedOption(): CLPSelectedOptionContext {
    const context = useContext(ReactCLPSelectedOptionContext);

    if (context === undefined) {
        throw new Error('useClpSelectedOption must be used within a CLPPurchaseContextProvider');
    }

    return context;
}
